import React, { useRef, useState } from 'react';
import ActionButton from '../../../../shared/components/action-button/ActionButton';
import '../../../services/styles/services.css';

const ServiceImportExport = ({ 
  services, 
  onImport, 
  disabled = false 
}) => {
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const fileInputRef = useRef();
  const csvInputRef = useRef();

  const handleExport = () => {
    const exportData = {
      services: services.map(({ 
        name,
        description,
        duration, 
        bufferTime, 
        price, 
        imageUrl,
        addOns = [],
        packages = []
      }) => ({
        name,
        description: description || '',
        duration,
        bufferTime,
        price,
        imageUrl,
        addOns: addOns.map(addon => ({
          name: addon.name,
          price: addon.price,
          duration: addon.duration,
          imageUrl: addon.imageUrl
        })),
        packages: packages.map(pkg => ({
          name: pkg.name,
          description: pkg.description,
          basePrice: pkg.basePrice,
          currentPrice: pkg.currentPrice,
          discount: pkg.discount,
          isDiscountApplied: pkg.isDiscountApplied,
          duration: pkg.duration,
          bufferTime: pkg.bufferTime,
          imageUrl: pkg.imageUrl,
          terms: pkg.terms,
          selectedAddons: pkg.selectedAddons
        }))
      })),
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `services-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    // Create CSV headers
    const headers = [
      'Service Name',
      'Duration (min)',
      'Buffer Time (min)',
      'Price'
    ];

    // Create rows for each service
    const rows = services.map(service => [
      service.name,
      service.duration,
      service.bufferTime,
      service.price
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `services-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadCSVTemplate = () => {
    const templateContent = 'Service Name,Duration (min),Buffer Time (min),Price\nExample Service,60,15,99.99\nAnother Service,30,10,49.99';
    const blob = new Blob([templateContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'service-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const validateImportData = (data) => {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid file format');
    }

    if (!Array.isArray(data.services)) {
      throw new Error('Invalid services data');
    }

    data.services.forEach((service, index) => {
      if (!service.name || typeof service.name !== 'string') {
        throw new Error(`Invalid service name at index ${index}`);
      }
      if (typeof service.duration !== 'number' || service.duration <= 0) {
        throw new Error(`Invalid duration for service "${service.name}"`);
      }
      if (typeof service.price !== 'number' || service.price < 0) {
        throw new Error(`Invalid price for service "${service.name}"`);
      }
      
      // Validate add-ons if present
      if (service.addOns) {
        if (!Array.isArray(service.addOns)) {
          throw new Error(`Invalid add-ons format for service "${service.name}"`);
        }
        service.addOns.forEach((addon, addonIndex) => {
          if (!addon.name || typeof addon.name !== 'string') {
            throw new Error(`Invalid add-on name in service "${service.name}"`);
          }
          if (typeof addon.price !== 'number' || addon.price < 0) {
            throw new Error(`Invalid add-on price in service "${service.name}"`);
          }
        });
      }

      // Validate packages if present
      if (service.packages) {
        if (!Array.isArray(service.packages)) {
          throw new Error(`Invalid packages format for service "${service.name}"`);
        }
        service.packages.forEach((pkg, pkgIndex) => {
          if (!pkg.name || typeof pkg.name !== 'string') {
            throw new Error(`Invalid package name in service "${service.name}"`);
          }
          if (typeof pkg.basePrice !== 'number' || pkg.basePrice < 0) {
            throw new Error(`Invalid package price in service "${service.name}"`);
          }
        });
      }
    });

    return true;
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').map(line => line.trim()).filter(line => line);
    const headers = lines[0].split(',').map(header => header.trim());
    
    if (!headers.includes('Service Name') || !headers.includes('Duration (min)') || !headers.includes('Price')) {
      throw new Error('Invalid CSV format. Please use the template provided.');
    }

    const services = lines.slice(1).map((line, index) => {
      const values = line.split(',').map(value => value.trim());
      const serviceData = {
        name: values[headers.indexOf('Service Name')],
        duration: parseInt(values[headers.indexOf('Duration (min)')]),
        bufferTime: parseInt(values[headers.indexOf('Buffer Time (min)')]) || 0,
        price: parseFloat(values[headers.indexOf('Price')])
      };

      if (!serviceData.name) {
        throw new Error(`Missing service name on line ${index + 2}`);
      }
      if (isNaN(serviceData.duration) || serviceData.duration <= 0) {
        throw new Error(`Invalid duration for service "${serviceData.name}"`);
      }
      if (isNaN(serviceData.price) || serviceData.price < 0) {
        throw new Error(`Invalid price for service "${serviceData.name}"`);
      }

      return serviceData;
    });

    return { services };
  };

  const handleFileSelect = (event, isCSV = false) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let data;
        if (isCSV) {
          data = parseCSV(e.target.result);
        } else {
          data = JSON.parse(e.target.result);
          validateImportData(data);
        }
        onImport(data.services);
        setImportDialogOpen(false);
        setImportError('');
      } catch (error) {
        setImportError(error.message || 'Failed to parse import file');
      }
    };
    reader.onerror = () => {
      setImportError('Failed to read file');
    };
    reader.readAsText(file);
  };

  return (
    <div className="svc-page" style={{ padding: 0 }}>
      <div className="app-action-toolbar">
        <ActionButton variant="info" onClick={() => setImportDialogOpen(true)} disabled={disabled}>
          Import
        </ActionButton>
        <ActionButton
          variant="success"
          onClick={handleExportCSV}
          disabled={disabled || services.length === 0}
        >
          Export as CSV
        </ActionButton>
      </div>

      {importDialogOpen && (
        <div className="svc-modal-backdrop" onClick={() => { setImportDialogOpen(false); setImportError(''); setSelectedTab(0); }}>
          <div className="svc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="svc-modal__header">Import Services</div>
            <div className="svc-modal__body">
              <div className="svc-tabs">
                <div className={`svc-tab ${selectedTab === 0 ? 'svc-tab--active' : ''}`} onClick={() => setSelectedTab(0)}>CSV (Excel)</div>
              </div>

              {importError && (
                <div className="svc-alert svc-alert--error" style={{ marginTop: 12 }}>{importError}</div>
              )}

              {selectedTab === 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 14, color: 'var(--text-700)', marginBottom: 8 }}>
                    Import basic service information from a CSV file. For full service data including add-ons and packages, use JSON format.
                  </div>
                  <ActionButton onClick={downloadCSVTemplate}>
                    Download CSV Template
                  </ActionButton>
                  <input type="file" ref={csvInputRef} accept=".csv" onChange={(e) => handleFileSelect(e, true)} style={{ display: 'none' }} />
                  <div style={{ marginTop: 10 }}>
                    <ActionButton variant="primary" onClick={() => csvInputRef.current.click()}>
                      Select CSV File
                    </ActionButton>
                  </div>
                </div>
              )}

              {false && selectedTab === 1}
            </div>
            <div className="svc-modal__footer">
              <ActionButton onClick={() => { setImportDialogOpen(false); setImportError(''); setSelectedTab(0); }}>
                Cancel
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceImportExport;